import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Sparkles, Building2, Globe, MapPin, Users,
  ListPlus, FilterX, Check, X, Database, ChevronLeft,
  ChevronRight, Phone, Mail, Linkedin, Info, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import MultiSelectPopover from "@/components/MultiSelectPopover";
import TitleAutocomplete from "@/components/TitleAutocomplete";
import { searchPeople, revealContact, usingClickHouse, fetchFacets } from "@/lib/searchClient";
import { LINKEDIN_INDUSTRIES } from "@/lib/industries";
import { useRevealPrices } from "@/lib/pricing";
import { useBillingDialogs } from "@/contexts/BillingDialogs";

// ── Constants ─────────────────────────────────────────────────────────────────
const INDUSTRIES = LINKEDIN_INDUSTRIES;
const SIZE_OPTIONS = ["1-10","11-50","51-200","201-500","500-1000","1000+"];
const REVENUE_BANDS = [
  { label: "<$1M",       value: "<1M"      },
  { label: "$1M–$10M",   value: "1M-10M"   },
  { label: "$10M–$50M",  value: "10M-50M"  },
  { label: "$50M–$100M", value: "50M-100M" },
  { label: "$100M–$500M",value: "100M-500M"},
  { label: "$500M+",     value: "500M+"    },
];
const SENIORITY_OPTIONS = [
  "Entry","Senior","Manager","Director","VP","C-Suite","Owner/Founder",
];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Only filters the contact catalog can actually back. Fields absent from the
// bulk data (email_status, technologies, keywords, departments, founded year,
// SIC/NAICS) were removed rather than left to silently match nothing.
const defaultPeopleFilters = {
  query: "", name: "",
  title: "", title_exclude: "",
  seniority: [],
  has_phone: false,
  location: "", city: "", state: "", country: "",
  company_name: "", company_name_exclude: "", company_domain: "",
  industry: [],
  size: [], employee_min: "", employee_max: "",
  revenue_band: [],
  company_location: "",
};

const defaultCompanyFilters = {
  query: "",
  industry: [], size: [],
  employee_min: "", employee_max: "",
  revenue_band: [], revenue_min: "", revenue_max: "",
  location: "", keywords: "", technologies: "",
  founded_year_min: "", founded_year_max: "",
  company_type: "", sic_codes: "", naics_codes: "",
};


function countFilters(f, defaults) {
  return Object.keys(defaults).filter(k => {
    const v = f[k], d = defaults[k];
    if (Array.isArray(d)) return v.length > 0;
    return v !== d;
  }).length;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Research() {
  const { user, refreshUser } = useAuth();
  const billing = useBillingDialogs();

  // Tab (people | companies) — persisted in localStorage
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("il_research_tab") || "people"; } catch { return "people"; }
  });
  const switchTab = (t) => {
    setTab(t);
    try { localStorage.setItem("il_research_tab", t); } catch {}
    setSelected(null); setSelectedIds(new Set());
  };

  // Data
  const [rows, setRows]           = useState(null);   // null = never loaded yet
  // Distinct from `rows === null`: that is the FIRST load (skeleton). This is
  // any search in flight, including a re-search where we keep the old rows on
  // screen. It also gates Apply, so an impatient double-click cannot queue two
  // identical, expensive queries.
  const [searching, setSearching]  = useState(false);
  // Which search is current. Reveal hydration lands after the rows do, so a
  // slow one from a superseded search must not overwrite newer results.
  const searchSeq = useRef(0);
  const [totalCount, setTotal]    = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(25);

  // Filters are held twice on purpose.
  //
  // `pf`/`cf` are the DRAFT — what the inputs are bound to. `appliedPf`/
  // `appliedCf` are what actually drives the query. Previously the query read
  // the draft directly, so every keystroke in a text field fired a search:
  // typing "bloomberg" sent nine requests, eight of them for prefixes nobody
  // wanted, and each substring search is one of the most expensive queries we
  // run. Committing on Apply/Enter cuts request volume roughly 5-10x.
  const [pf, setPf] = useState(defaultPeopleFilters);
  const [appliedPf, setAppliedPf] = useState(defaultPeopleFilters);
  const [cf, setCf] = useState(defaultCompanyFilters);
  const [appliedCf, setAppliedCf] = useState(defaultCompanyFilters);

  // Commit the draft. Also used by the chips and Clear all, which are explicit
  // actions and so should take effect immediately rather than wait for Apply.
  const applyPeople  = (next) => { const v = next ?? pf; setPf(v); setAppliedPf(v); setPage(0); };
  const applyCompany = (next) => { const v = next ?? cf; setCf(v); setAppliedCf(v); setPage(0); };

  // Selection & UI
  const [selected, setSelected]   = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [revealing, setRevealing] = useState(null); // `${id}:${type}` while in flight
  const [lists, setLists]         = useState([]);
  // Dropdown vocabularies, read from the catalog rather than hardcoded. null
  // until loaded, and stays null on the Supabase path, where the static lists
  // are the correct ones.
  const [facets, setFacets]       = useState(null);
  const [newListName, setNewListName] = useState("");

  // ── Fetch data ──
  const buildPeopleParams = useCallback(() => ({
    p_query:               appliedPf.query      || null,
    p_name:                appliedPf.name       || null,
    p_title:               appliedPf.title      || null,
    p_title_exclude:       appliedPf.title_exclude || null,
    p_seniority:           appliedPf.seniority.length ? appliedPf.seniority : null,
    p_departments:         null,
    p_email_status:        null,
    p_has_phone:           appliedPf.has_phone ? true : null,
    p_location:            appliedPf.location   || null,
    p_city:                appliedPf.city       || null,
    p_state:               appliedPf.state      || null,
    p_country:             appliedPf.country    || null,
    p_company_name:        appliedPf.company_name || null,
    p_company_name_exclude:appliedPf.company_name_exclude || null,
    p_company_domain:      appliedPf.company_domain || null,
    p_industry:            appliedPf.industry.length ? appliedPf.industry : null,
    p_keywords:            null,
    p_technologies:        null,
    p_size:                appliedPf.size.length ? appliedPf.size : null,
    p_employee_min:        appliedPf.employee_min !== "" ? parseInt(appliedPf.employee_min) : null,
    p_employee_max:        appliedPf.employee_max !== "" ? parseInt(appliedPf.employee_max) : null,
    p_revenue_band:        appliedPf.revenue_band.length ? appliedPf.revenue_band : null,
    p_revenue_min:         null,
    p_revenue_max:         null,
    p_company_location:    appliedPf.company_location || null,
    p_founded_year_min:    null,
    p_founded_year_max:    null,
    p_sic_codes:           null,
    p_naics_codes:         null,
    p_limit:               pageSize,
    p_offset:              page * pageSize,
  }), [appliedPf, page, pageSize]);

  const buildCompanyParams = useCallback(() => ({
    p_query:           appliedCf.query    || null,
    p_industry:        appliedCf.industry.length ? appliedCf.industry : null,
    p_size:            appliedCf.size.length ? appliedCf.size : null,
    p_employee_min:    appliedCf.employee_min !== "" ? parseInt(appliedCf.employee_min) : null,
    p_employee_max:    appliedCf.employee_max !== "" ? parseInt(appliedCf.employee_max) : null,
    p_revenue_band:    appliedCf.revenue_band.length ? appliedCf.revenue_band : null,
    p_revenue_min:     appliedCf.revenue_min !== "" ? parseFloat(appliedCf.revenue_min) : null,
    p_revenue_max:     appliedCf.revenue_max !== "" ? parseFloat(appliedCf.revenue_max) : null,
    p_location:        appliedCf.location  || null,
    p_keywords:        appliedCf.keywords  || null,
    p_technologies:    appliedCf.technologies || null,
    p_founded_year_min:appliedCf.founded_year_min !== "" ? parseInt(appliedCf.founded_year_min) : null,
    p_founded_year_max:appliedCf.founded_year_max !== "" ? parseInt(appliedCf.founded_year_max) : null,
    p_company_type:    appliedCf.company_type || null,
    p_sic_codes:       appliedCf.sic_codes || null,
    p_naics_codes:     appliedCf.naics_codes || null,
    p_limit:           pageSize,
    p_offset:          page * pageSize,
  }), [appliedCf, page, pageSize]);

  const fetchRows = useCallback(async () => {
    setSearching(true);
    const seq = ++searchSeq.current;
    try {
    if (tab === "people") {
      // Routed by searchClient: Supabase RPC, or the ClickHouse API when
      // REACT_APP_SEARCH_API_URL is set.
      try {
        const { rows: found, total, totalIsCapped } = await searchPeople(appliedPf, {
          limit: pageSize,
          offset: page * pageSize,
          supabaseParams: buildPeopleParams(),
          // Arrives after the rows: paid emails and phone numbers, merged in
          // place so a reveal the user made meanwhile is not clobbered.
          onHydrated: (patched) => {
            if (seq !== searchSeq.current) return;
            const byId = new Map(patched.map((r) => [r.id, r]));
            setRows((prev) => (prev || []).map((r) => {
              const p = byId.get(r.id);
              return p && (p.email_revealed || p.phone_revealed || p.company_phone_revealed)
                ? { ...r, ...p } : r;
            }));
          },
        });
        if (seq !== searchSeq.current) return;
        setRows(found);
        setTotal(total);
        setTotalCapped(totalIsCapped);
      } catch (e) {
        toast.error(e.message || "Search failed");
        setRows([]);
      }
    } else {
      const params = buildCompanyParams();
      const countParams = { ...params }; delete countParams.p_limit; delete countParams.p_offset;
      const [dataRes, countRes] = await Promise.all([
        supabase.rpc("search_companies", params),
        supabase.rpc("count_companies", countParams),
      ]);
      if (dataRes.error) { toast.error(dataRes.error.message); setRows([]); return; }
      setRows(dataRes.data || []);
      setTotal(Number(countRes.data) || 0);
    }
    } finally {
      // Only the newest search owns the spinner: a superseded one finishing
      // must not clear it while its replacement is still running.
      if (seq === searchSeq.current) setSearching(false);
    }
  }, [tab, appliedPf, page, pageSize, buildPeopleParams, buildCompanyParams]);

  const fetchLists = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase.from("user_lists").select("id, name").eq("user_id", user.id).order("name");
    if (data) setLists(data);
  }, [user?.id]);

  // People and company rows have different shapes, so never show one while the
  // other is loading.
  useEffect(() => { setPage(0); setRows(null); }, [tab, pageSize]);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => { fetchFacets().then(setFacets); }, []);

  // ── Reveal (people only): email and phone are independent ──
  const doReveal = async (e, personId, type) => {
    e.stopPropagation();
    setRevealing(`${personId}:${type}`);
    // revealContact routes to the Postgres RPC or, for catalog rows, to the
    // reveal-contact Edge Function. Both are charge-on-success.
    const res = await revealContact(personId, type);
    setRevealing(null);

    if (!res.ok) {
      // Out of credits, today's reveal limit and a paused account each open
      // their own pop-up (BillingDialogs); misses and outages stay toasts.
      if (billing.handleRevealError(res)) {
        // handled
      } else if (res.error === "no_value") {
        toast.error(type === "email" ? "No email on file for this contact." : "No phone number on file for this contact.");
      } else if (res.error === "lookup_failed") {
        toast.error("Contact lookup is temporarily unavailable — you were not charged.");
      } else {
        toast.error("Could not reveal contact.");
      }
      return;
    }

    setRows(prev => (prev || []).map(r => r.id === personId ? { ...r, ...res.patch } : r));
    if (selected?.id === personId) setSelected(s => ({ ...s, ...res.patch }));
    if (!res.alreadyRevealed) refreshUser();
  };
  const revealEmail = (e, personId) => doReveal(e, personId, "email");
  const revealPhone = (e, personId) => doReveal(e, personId, "phone");
  const revealCompanyPhone = (e, personId) => doReveal(e, personId, "company_phone");

  // ── Selection ──
  const filtered = useMemo(() => rows || [], [rows]);
  const allSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.id));
  const toggleAll  = () => setSelectedIds(prev => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach(r => n.delete(r.id));
    else filtered.forEach(r => n.add(r.id));
    return n;
  });
  const toggleOne = id => setSelectedIds(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  // ── Add to list ──
  const addToList = async (listId, listName) => {
    if (selectedIds.size === 0) { toast.error("Select at least one row"); return; }
    if (!user?.id) return;
    let targetId = listId;
    if (!targetId) {
      const trimmed = (listName || "").trim();
      if (!trimmed) { toast.error("Enter a list name"); return; }
      const { data, error } = await supabase.from("user_lists")
        .insert({ user_id: user.id, name: trimmed }).select().single();
      if (error) { toast.error(error.message); return; }
      targetId = data.id;
      await fetchLists();
    }
    const col   = tab === "people" ? "person_id" : "company_id";
    const etype = tab === "people" ? "person" : "company";
    const rows2 = Array.from(selectedIds).map(id => ({
      list_id: targetId, [col]: id, entity_type: etype,
    }));
    const { error } = await supabase.from("list_contacts").upsert(rows2, { ignoreDuplicates: true });
    if (error) { toast.error(error.message); return; }
    toast.success(`Added ${selectedIds.size} to list`);
    setNewListName("");
  };

  // ── Counts ──
  const pFilterCount = countFilters(appliedPf, defaultPeopleFilters);
  const cFilterCount = countFilters(appliedCf, defaultCompanyFilters);
  const activeFilterCount = tab === "people" ? pFilterCount : cFilterCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rowStart   = totalCount === 0 ? 0 : page * pageSize + 1;
  const rowEnd     = Math.min((page + 1) * pageSize, totalCount);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Top bar ── */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-3 md:px-8">
        <div className="flex items-center gap-3">
          {/* Tab switcher */}
          <div className="flex items-center rounded-md border border-border bg-surface-inset p-0.5">
            {["people","companies"].map(t => (
              <button key={t} onClick={() => switchTab(t)}
                className={`rounded-sm px-3 py-1 text-sm capitalize transition-colors ${
                  tab === t ? "bg-surface text-foreground font-medium shadow-card" : "text-muted-foreground hover:text-foreground"
                }`}
              >{t}</button>
            ))}
          </div>
          <h1 className="font-display text-xl tracking-tight hidden md:block">Research</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 && (
            <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
          )}

          {/* Add to list */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" disabled={selectedIds.size === 0}>
                <ListPlus className="h-3.5 w-3.5" /> Add to list
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-3">
              <div className="eyebrow">Add {selectedIds.size} to list</div>
              {lists.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {lists.map(l => (
                    <button key={l.id} onClick={() => addToList(l.id, l.name)}
                      className="rounded-md border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-hover">
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input placeholder="New list name" value={newListName}
                  onChange={e => setNewListName(e.target.value)} />
                <Button size="sm" onClick={() => addToList(null, newListName)}>Add</Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* ── Filter chips row ── */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-6 py-2 md:px-8">
          <span className="text-[11px] text-muted-foreground uppercase tracking-[0.06em]">Filters:</span>
          {tab === "people"
            ? <PeopleFilterChips pf={appliedPf} setPf={applyPeople} />
            : <CompanyFilterChips cf={appliedCf} setCf={applyCompany} />
          }
          <button onClick={() => tab === "people" ? applyPeople(defaultPeopleFilters) : applyCompany(defaultCompanyFilters)}
            className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-surface-hover">
            <FilterX className="h-3 w-3" /> Clear all
          </button>
        </div>
      )}

      {/* ── Body: filters sidebar + table ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Filter sidebar */}
        <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-border overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="eyebrow">Filters</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{activeFilterCount}</Badge>
              )}
            </div>
            <button onClick={() => tab === "people" ? applyPeople(defaultPeopleFilters) : applyCompany(defaultCompanyFilters)}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
              <FilterX className="h-3 w-3" />
            </button>
          </div>

          {tab === "people"
            ? <PeopleFilters pf={pf} setPf={setPf} onApply={() => applyPeople()} busy={searching} facets={facets} />
            : <CompanyFilters cf={cf} setCf={setCf} onApply={() => applyCompany()} busy={searching} />
          }
        </aside>

        {/* Table area */}
        <div className="flex flex-1 flex-col overflow-hidden bg-surface">
          {/* Pagination + page size bar */}
          <div className="flex items-center justify-between border-b border-border px-5 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {/* An exact count over 146M rows is far slower than the page
                  itself, so the API caps counting at 100k and flags it. */}
              {searching ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                </span>
              ) : totalCount > 0
                ? `Showing ${rowStart.toLocaleString()}–${rowEnd.toLocaleString()} of ${totalCount.toLocaleString()}${totalCapped ? "+" : ""}`
                : "No results"}
              {usingClickHouse() && tab === "people" && (
                <Badge variant="outline" className="gap-1 py-0 text-[10px]">
                  <Database className="h-2.5 w-2.5" /> catalog
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
                <SelectTrigger className="h-7 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <SelectItem key={n} value={String(n)} className="text-xs">{n} / page</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="rounded p-1 text-muted-foreground hover:bg-surface-hover disabled:opacity-30">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-muted-foreground">{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="rounded p-1 text-muted-foreground hover:bg-surface-hover disabled:opacity-30">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className={`flex-1 overflow-auto transition-opacity ${
            searching && rows !== null ? "pointer-events-none opacity-50" : ""}`}>
            {rows === null ? (
              <div className="space-y-3 p-5">
                {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-11 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState />
            ) : tab === "people" ? (
              <PeopleTable rows={filtered} allSelected={allSelected} toggleAll={toggleAll}
                toggleOne={toggleOne} selectedIds={selectedIds}
                setSelected={setSelected} revealEmail={revealEmail} revealPhone={revealPhone}
                revealCompanyPhone={revealCompanyPhone} revealing={revealing} />
            ) : (
              <CompanyTable rows={filtered} allSelected={allSelected} toggleAll={toggleAll}
                toggleOne={toggleOne} selectedIds={selectedIds} setSelected={setSelected} />
            )}
          </div>
        </div>
      </div>

      {/* ── Detail drawer ── */}
      <DetailDrawer
        item={selected}
        tab={tab}
        onOpenChange={o => { if (!o) setSelected(null); }}
        onUpdate={updated => {
          setRows(prev => (prev || []).map(r => r.id === updated.id ? updated : r));
          setSelected(updated);
        }}
        revealEmail={revealEmail}
        revealPhone={revealPhone}
        revealCompanyPhone={revealCompanyPhone}
        revealing={revealing}
      />
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center py-20 text-center">
      <Database className="h-16 w-16 text-muted-foreground opacity-40" />
      <h3 className="mt-4 font-display text-lg tracking-tight">No results</h3>
      <p className="mt-1 text-sm text-muted-foreground">Try adjusting your filters or search query.</p>
    </div>
  );
}

// ── People table ──────────────────────────────────────────────────────────────
function PeopleTable({ rows, allSelected, toggleAll, toggleOne, selectedIds, setSelected,
                      revealEmail, revealPhone, revealCompanyPhone, revealing }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border bg-surface-inset text-left text-xs font-medium text-muted-foreground">
          <th className="w-10 px-4 py-2.5">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
          </th>
          <th className="px-3 py-2.5 font-medium">Name</th>
          <th className="px-3 py-2.5 font-medium">Title</th>
          <th className="px-3 py-2.5 font-medium">Company</th>
          <th className="px-3 py-2.5 font-medium">Email</th>
          <th className="px-3 py-2.5 font-medium">Mobile / Direct</th>
          <th className="px-3 py-2.5 font-medium">Company number</th>
          <th className="px-3 py-2.5 font-medium">Location</th>
          <th className="px-3 py-2.5 font-medium"># Employees</th>
          <th className="px-3 py-2.5 font-medium">Industry</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(p => (
          <tr key={p.id} className={`cursor-pointer border-b border-border/60 ${selectedIds.has(p.id) ? "bg-violet-soft" : "hover:bg-surface-hover"}`}
            onClick={() => setSelected(p)}>
            <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
              <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleOne(p.id)} />
            </td>
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-soft text-violet-text text-xs font-semibold">
                  {(p.first_name?.[0] || p.last_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-medium leading-tight truncate">
                    {p.full_name || `${p.first_name} ${p.last_name}`.trim() || "—"}
                  </div>
                  {p.linkedin_url && (
                    <a href={p.linkedin_url} target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-[10px] text-violet-text hover:underline">
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </td>
            <td className="px-3 py-2.5">
              <div className="truncate max-w-[160px]">{p.title || "—"}</div>
              {p.seniority && <div className="text-[10px] text-muted-foreground">{p.seniority}</div>}
            </td>
            <td className="px-3 py-2.5">
              <div className="font-medium truncate max-w-[140px]">{p.company_name || "—"}</div>
              <div className="text-xs text-muted-foreground truncate">{p.company_domain}</div>
            </td>
            <td className="px-3 py-2.5">
              {(p.has_email ?? !!p.email) ? (
                p.email_revealed ? (
                  <div className="flex items-center gap-1.5">
                    <Check className="h-3 w-3 shrink-0 text-success" />
                    <span className="truncate max-w-[160px] text-xs">{p.email}</span>
                  </div>
                ) : (
                  <RevealInline type="email" onReveal={e => revealEmail(e, p.id)} loading={revealing === `${p.id}:email`} />
                )
              ) : <span className="text-muted-foreground">—</span>}
              {p.email_revealed && p.email_status === "verified" && (
                <div className="text-[10px] text-success">{p.email_status}</div>
              )}
            </td>
            {/* Mobile / direct — personal, credit-gated behind a reveal. */}
            <td className="px-3 py-2.5 text-xs text-muted-foreground">
              {p.phone_revealed ? (
                (p.mobile_phone || p.work_direct_phone || p.work_direct_phone_2 || p.home_phone) ? (
                  <div className="space-y-0.5">
                    {p.mobile_phone && <div className="whitespace-nowrap">{p.mobile_phone} <span className="text-[10px] text-muted-foreground/70">mobile</span></div>}
                    {p.work_direct_phone && <div className="whitespace-nowrap">{p.work_direct_phone} <span className="text-[10px] text-muted-foreground/70">direct</span></div>}
                    {p.work_direct_phone_2 && <div className="whitespace-nowrap">{p.work_direct_phone_2} <span className="text-[10px] text-muted-foreground/70">direct 2</span></div>}
                    {p.home_phone && <div className="whitespace-nowrap">{p.home_phone} <span className="text-[10px] text-muted-foreground/70">home</span></div>}
                  </div>
                ) : <span>—</span>
              ) : p.has_phone ? (
                <RevealInline type="phone" onReveal={e => revealPhone(e, p.id)} loading={revealing === `${p.id}:phone`} />
              ) : <span>—</span>}
            </td>
            {/* Company switchboard — shared by the whole org, so it is public
                firmographic data and never costs a credit. */}
            <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
              {(p.company_phone_revealed || p.company_phone)
                ? (p.company_phone || p.corporate_phone || "—")
                : p.has_company_phone
                  ? <RevealInline type="company_phone" onReveal={e => revealCompanyPhone(e, p.id)} loading={revealing === `${p.id}:company_phone`} />
                  : "—"}
            </td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[130px]">
              {p.person_location || "—"}
            </td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
              {p.company_employee_count ? p.company_employee_count.toLocaleString() : p.company_size || "—"}
            </td>
            <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[130px]">
              {p.company_industry || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Company table ─────────────────────────────────────────────────────────────
function CompanyTable({ rows, allSelected, toggleAll, toggleOne, selectedIds, setSelected }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border bg-surface-inset text-left text-xs font-medium text-muted-foreground">
          <th className="w-10 px-4 py-2.5">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
          </th>
          <th className="px-3 py-2.5 font-medium">Company</th>
          <th className="px-3 py-2.5 font-medium">Industry</th>
          <th className="px-3 py-2.5 font-medium"># Employees</th>
          <th className="px-3 py-2.5 font-medium">Location</th>
          <th className="px-3 py-2.5 font-medium">Keywords</th>
          <th className="px-3 py-2.5 font-medium">Revenue</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(c => (
          <tr key={c.id} className={`cursor-pointer border-b border-border/60 ${selectedIds.has(c.id) ? "bg-violet-soft" : "hover:bg-surface-hover"}`}
            onClick={() => setSelected(c)}>
            <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
              <Checkbox checked={selectedIds.has(c.id)} onCheckedChange={() => toggleOne(c.id)} />
            </td>
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-border bg-surface-inset text-ink-secondary text-xs font-semibold">
                  {(c.name?.[0] || "?").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-medium leading-tight truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground">{c.domain}</div>
                </div>
              </div>
            </td>
            <td className="px-3 py-2.5 text-muted-foreground">{c.industry || "—"}</td>
            <td className="px-3 py-2.5 text-muted-foreground">
              {c.size || (c.employee_count ? c.employee_count.toLocaleString() : "—")}
            </td>
            <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[130px]">{c.hq_location || "—"}</td>
            <td className="px-3 py-2.5">
              <div className="flex flex-wrap gap-1">
                {(c.keywords || c.tags || []).slice(0, 3).map(k => (
                  <Badge key={k} variant="outline" className="text-[10px] py-0">{k}</Badge>
                ))}
                {(c.keywords || c.tags || []).length > 3 && (
                  <Badge variant="outline" className="text-[10px] py-0">+{(c.keywords || c.tags || []).length - 3}</Badge>
                )}
              </div>
            </td>
            <td className="px-3 py-2.5 text-muted-foreground">{c.revenue_band || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Apply, disabled while a search is in flight so a double-click can't queue
 *  two identical, expensive queries. */
function ApplyButton({ onApply, busy }) {
  return (
    <Button className="w-full" size="sm" onClick={onApply} disabled={busy} data-testid="apply-filters">
      {busy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Searching…</> : "Apply filters"}
    </Button>
  );
}

// ── People filters sidebar ────────────────────────────────────────────────────
/** Compact row count for a dropdown option: 15230985 -> "15.2M". */
function shortCount(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
  return String(n);
}

/**
 * Options for one dropdown: the catalog's own values when we have them,
 * otherwise the static list. Counts are shown because they are the difference
 * between picking a filter that returns 15M rows and one that returns 23,000.
 */
function facetOptions(facets, key, fallback) {
  const rows = facets?.[key];
  if (!rows?.length) return fallback;
  return rows.map((r) => ({ label: `${r.value}  ·  ${shortCount(r.count)}`, value: r.value }));
}

function PeopleFilters({ pf, setPf, onApply, busy, facets }) {
  // Enter anywhere in the panel commits, matching the Apply button — keydown
  // bubbles, so this covers every input without touching each one.
  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); onApply(); } };
  const set = k => v => setPf(f => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-col" onKeyDown={onKeyDown}>
      {/* Apply sits at the top and sticks: the panel is taller than the
          viewport, so a button only at the bottom means scrolling past every
          filter to run the search you just set up. */}
      <div className="sticky top-0 z-20 border-b border-border bg-background px-4 py-3">
        <ApplyButton onApply={onApply} busy={busy} />
      </div>
      <div className="px-4 py-3">
        <Label className="text-xs text-muted-foreground">Search</Label>
        <div className="relative mt-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={pf.query} onChange={e => setPf(f => ({ ...f, query: e.target.value }))}
            placeholder="Name, title, email…" className="pl-7 h-8 text-sm" />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["contact","title","seniority","company","location","firmographics"]} className="px-2">
        <FilterSection value="contact" label="Contact">
          {/* "has_phone" means a PERSONAL line (mobile / direct / home) — the
              company switchboard is deliberately excluded, since a main line
              shared by thousands of employees isn't a contactable number. */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={pf.has_phone} onCheckedChange={v => set("has_phone")(!!v)} data-testid="pf-has-phone" />
            Has mobile or direct number
          </label>
        </FilterSection>

        <FilterSection value="title" label="Job Title">
          <Label className="text-xs text-muted-foreground">Include</Label>
          {/* Titles are free text with a very long tail, so we suggest the
              exact wordings that exist in the catalog, most-common first. */}
          <TitleAutocomplete value={pf.title} onChange={set("title")}
            placeholder="VP Sales, CEO…" testid="pf-title" />
          <Label className="mt-2 text-xs text-muted-foreground">Exclude</Label>
          <Input value={pf.title_exclude} onChange={e => set("title_exclude")(e.target.value)} placeholder="Intern, Assistant…" className="mt-1 h-8 text-sm" />
        </FilterSection>

        <FilterSection value="seniority" label="Seniority">
          <MultiSelectPopover
            options={facetOptions(facets, "seniority", SENIORITY_OPTIONS.map(s => ({ label: s, value: s })))}
            values={pf.seniority} onChange={set("seniority")}
            placeholder="Any seniority" testid="pf-seniority" />
        </FilterSection>

        <FilterSection value="company" label="Company">
          <Label className="text-xs text-muted-foreground">Include</Label>
          <Input value={pf.company_name} onChange={e => set("company_name")(e.target.value)} placeholder="Stripe, Notion…" className="mt-1 h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">Exclude</Label>
          <Input value={pf.company_name_exclude} onChange={e => set("company_name_exclude")(e.target.value)} placeholder="Acme…" className="mt-1 h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">Domain</Label>
          <Input value={pf.company_domain} onChange={e => set("company_domain")(e.target.value)} placeholder="stripe.com" className="mt-1 h-8 text-sm" />
        </FilterSection>

        <FilterSection value="location" label="Location">
          <Input value={pf.location} onChange={e => set("location")(e.target.value)} placeholder="City, state, or country" className="h-8 text-sm" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input value={pf.city} onChange={e => set("city")(e.target.value)} placeholder="City" className="h-8 text-sm" />
            <Input value={pf.state} onChange={e => set("state")(e.target.value)} placeholder="State" className="h-8 text-sm" />
          </div>
          <Input value={pf.country} onChange={e => set("country")(e.target.value)} placeholder="Country" className="mt-2 h-8 text-sm" />
        </FilterSection>

        <FilterSection value="firmographics" label="Company Size & Revenue">
          <Label className="text-xs text-muted-foreground">Industry</Label>
          <MultiSelectPopover options={facetOptions(facets, "industry", INDUSTRIES.map(i => ({ label: i, value: i })))}
            values={pf.industry} onChange={set("industry")} placeholder="Select industries" testid="pf-industry" />
          <Label className="mt-2 text-xs text-muted-foreground">Employee size</Label>
          <MultiSelectPopover options={facetOptions(facets, "company_size", SIZE_OPTIONS.map(s => ({ label: s, value: s })))}
            values={pf.size} onChange={set("size")} placeholder="Select sizes" testid="pf-size" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input type="number" value={pf.employee_min} onChange={e => set("employee_min")(e.target.value)} placeholder="Min" className="h-8 text-sm" />
            <Input type="number" value={pf.employee_max} onChange={e => set("employee_max")(e.target.value)} placeholder="Max" className="h-8 text-sm" />
          </div>
          <Label className="mt-2 text-xs text-muted-foreground">Revenue band</Label>
          <MultiSelectPopover options={facetOptions(facets, "revenue_band", REVENUE_BANDS)} values={pf.revenue_band}
            onChange={set("revenue_band")} placeholder="Select bands" testid="pf-revenue-band" />
        </FilterSection>

        <FilterSection value="more" label="More Filters">
          <Label className="text-xs text-muted-foreground">Name</Label>
          <Input value={pf.name} onChange={e => set("name")(e.target.value)} placeholder="John…" className="mt-1 h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">Company location</Label>
          <Input value={pf.company_location} onChange={e => set("company_location")(e.target.value)} placeholder="San Francisco…" className="mt-1 h-8 text-sm" />
        </FilterSection>
      </Accordion>

      <div className="px-4 py-4">
        <ApplyButton onApply={onApply} busy={busy} />
      </div>
    </div>
  );
}

// ── Company filters sidebar ───────────────────────────────────────────────────
function CompanyFilters({ cf, setCf, onApply, busy }) {
  // Enter anywhere in the panel commits, matching the Apply button — keydown
  // bubbles, so this covers every input without touching each one.
  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); onApply(); } };
  const set = k => v => setCf(f => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-col" onKeyDown={onKeyDown}>
      {/* Apply sits at the top and sticks: the panel is taller than the
          viewport, so a button only at the bottom means scrolling past every
          filter to run the search you just set up. */}
      <div className="sticky top-0 z-20 border-b border-border bg-background px-4 py-3">
        <ApplyButton onApply={onApply} busy={busy} />
      </div>
      <div className="px-4 py-3">
        <Label className="text-xs text-muted-foreground">Search</Label>
        <div className="relative mt-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={cf.query} onChange={e => setCf(f => ({ ...f, query: e.target.value }))}
            placeholder="Name, domain, description…" className="pl-7 h-8 text-sm" />
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["industry","size","location","tech"]} className="px-2">
        <FilterSection value="industry" label="Industry">
          <MultiSelectPopover options={INDUSTRIES.map(i => ({ label: i, value: i }))}
            values={cf.industry} onChange={set("industry")} placeholder="Select industries" testid="cf-industry" />
        </FilterSection>

        <FilterSection value="size" label="Company Size">
          <MultiSelectPopover options={SIZE_OPTIONS.map(s => ({ label: s, value: s }))}
            values={cf.size} onChange={set("size")} placeholder="Select sizes" testid="cf-size" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input type="number" value={cf.employee_min} onChange={e => set("employee_min")(e.target.value)} placeholder="Min" className="h-8 text-sm" />
            <Input type="number" value={cf.employee_max} onChange={e => set("employee_max")(e.target.value)} placeholder="Max" className="h-8 text-sm" />
          </div>
        </FilterSection>

        <FilterSection value="revenue" label="Revenue">
          <MultiSelectPopover options={REVENUE_BANDS} values={cf.revenue_band}
            onChange={set("revenue_band")} placeholder="Select bands" testid="cf-revenue-band" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input type="number" value={cf.revenue_min} onChange={e => set("revenue_min")(e.target.value)} placeholder="Min $" className="h-8 text-sm" />
            <Input type="number" value={cf.revenue_max} onChange={e => set("revenue_max")(e.target.value)} placeholder="Max $" className="h-8 text-sm" />
          </div>
        </FilterSection>

        <FilterSection value="location" label="HQ Location">
          <Input value={cf.location} onChange={e => set("location")(e.target.value)} placeholder="San Francisco, US…" className="h-8 text-sm" />
        </FilterSection>

        <FilterSection value="tech" label="Technologies & Keywords">
          <Label className="text-xs text-muted-foreground">Technologies</Label>
          <Input value={cf.technologies} onChange={e => set("technologies")(e.target.value)} placeholder="Salesforce…" className="h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">Keywords</Label>
          <Input value={cf.keywords} onChange={e => set("keywords")(e.target.value)} placeholder="SaaS, fintech…" className="mt-1 h-8 text-sm" />
        </FilterSection>

        <FilterSection value="more" label="More Filters">
          <Label className="text-xs text-muted-foreground">Founded year range</Label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <Input type="number" value={cf.founded_year_min} onChange={e => set("founded_year_min")(e.target.value)} placeholder="2000" className="h-8 text-sm" />
            <Input type="number" value={cf.founded_year_max} onChange={e => set("founded_year_max")(e.target.value)} placeholder="2024" className="h-8 text-sm" />
          </div>
          <Label className="mt-2 text-xs text-muted-foreground">Company type</Label>
          <Input value={cf.company_type} onChange={e => set("company_type")(e.target.value)} placeholder="for_profit, non_profit…" className="mt-1 h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">SIC codes</Label>
          <Input value={cf.sic_codes} onChange={e => set("sic_codes")(e.target.value)} placeholder="7372…" className="mt-1 h-8 text-sm" />
          <Label className="mt-2 text-xs text-muted-foreground">NAICS codes</Label>
          <Input value={cf.naics_codes} onChange={e => set("naics_codes")(e.target.value)} placeholder="541512…" className="mt-1 h-8 text-sm" />
        </FilterSection>
      </Accordion>

      <div className="px-4 py-4">
        <ApplyButton onApply={onApply} busy={busy} />
      </div>
    </div>
  );
}

// ── Filter section wrapper ────────────────────────────────────────────────────
function FilterSection({ value, label, children }) {
  return (
    <AccordionItem value={value}>
      <AccordionTrigger className="px-2 py-2 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground hover:text-foreground">
        {label}
      </AccordionTrigger>
      <AccordionContent className="space-y-1.5 px-2 pb-3">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

// ── Filter chips ──────────────────────────────────────────────────────────────
function PeopleFilterChips({ pf, setPf }) {
  const chips = [];
  const rm = k => () => setPf(f => ({ ...f, [k]: Array.isArray(f[k]) ? [] : "" }));
  if (pf.query) chips.push({ label: `"${pf.query}"`, remove: rm("query") });
  if (pf.title) chips.push({ label: `Title: ${pf.title}`, remove: rm("title") });
  pf.seniority.forEach(s => chips.push({ label: s, remove: () => setPf(f => ({ ...f, seniority: f.seniority.filter(x => x !== s) })) }));
  if (pf.has_phone)    chips.push({ label: "Has mobile/direct", remove: () => setPf(f => ({ ...f, has_phone: false })) });
  if (pf.company_name) chips.push({ label: `Co: ${pf.company_name}`, remove: rm("company_name") });
  if (pf.location)     chips.push({ label: `Loc: ${pf.location}`, remove: rm("location") });
  pf.industry.forEach(i => chips.push({ label: i, remove: () => setPf(f => ({ ...f, industry: f.industry.filter(x => x !== i) })) }));
  pf.size.forEach(s => chips.push({ label: s, remove: () => setPf(f => ({ ...f, size: f.size.filter(x => x !== s) })) }));
  return <>{chips.map((c, i) => <FilterChip key={i} label={c.label} onRemove={c.remove} />)}</>;
}
function CompanyFilterChips({ cf, setCf }) {
  const chips = [];
  const rm = k => () => setCf(f => ({ ...f, [k]: Array.isArray(f[k]) ? [] : "" }));
  if (cf.query) chips.push({ label: `"${cf.query}"`, remove: rm("query") });
  cf.industry.forEach(i => chips.push({ label: i, remove: () => setCf(f => ({ ...f, industry: f.industry.filter(x => x !== i) })) }));
  cf.size.forEach(s => chips.push({ label: s, remove: () => setCf(f => ({ ...f, size: f.size.filter(x => x !== s) })) }));
  if (cf.location)     chips.push({ label: `Loc: ${cf.location}`, remove: rm("location") });
  if (cf.keywords)     chips.push({ label: `KW: ${cf.keywords}`, remove: rm("keywords") });
  if (cf.technologies) chips.push({ label: `Tech: ${cf.technologies}`, remove: rm("technologies") });
  return <>{chips.map((c, i) => <FilterChip key={i} label={c.label} onRemove={c.remove} />)}</>;
}
function FilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-violet-soft px-2.5 py-0.5 text-xs font-medium text-violet-text">
      {label}
      <button onClick={onRemove} className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20">
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

// ── Detail drawer ─────────────────────────────────────────────────────────────
function DetailDrawer({ item, tab, onOpenChange, onUpdate,
                       revealEmail, revealPhone, revealCompanyPhone, revealing }) {
  const [notes, setNotes]       = useState("");
  const [summary, setSummary]   = useState("");
  const [outreach, setOutreach] = useState("");
  const [aiLoading, setAiLoad]  = useState(null);

  useEffect(() => {
    setNotes(item?.notes || "");
    setSummary(item?.ai_summary || "");
    setOutreach("");
  }, [item?.id]); // eslint-disable-line

  const saveNotes = async () => {
    const table = tab === "people" ? "people" : "companies";
    const { error } = await supabase.from(table).update({ notes }).eq("id", item.id);
    if (error) { toast.error(error.message); return; }
    onUpdate({ ...item, notes });
    toast.success("Notes saved");
  };

  const generate = async (kind) => {
    setAiLoad(kind);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const payload = tab === "people"
        ? { contact_id: item.company_id || null, person_id: item.id, prompt_type: kind }
        : { contact_id: item.id, prompt_type: kind };
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-summary`,
        { method: "POST",
          headers: { "Content-Type": "application/json",
                     "Authorization": `Bearer ${session.access_token}` },
          body: JSON.stringify(payload) }
      );
      const { text, error } = await res.json();
      if (error) { toast.error(error); return; }
      if (kind === "summary") { setSummary(text); onUpdate({ ...item, ai_summary: text }); }
      else setOutreach(text);
    } catch (e) {
      toast.error(e.message || "AI failed");
    } finally {
      setAiLoad(null);
    }
  };

  const isPerson = tab === "people";
  if (!item) return null;

  return (
    <Sheet open={!!item} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-accent text-accent-foreground font-display text-base">
              {isPerson
                ? (item.first_name?.[0] || item.last_name?.[0] || "?").toUpperCase()
                : (item.name?.[0] || "?").toUpperCase()
              }
            </div>
            <div>
              <SheetTitle className="font-display text-xl leading-none">
                {isPerson ? item.full_name || `${item.first_name} ${item.last_name}`.trim() : item.name}
              </SheetTitle>
              <SheetDescription className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                {isPerson && item.title && <span>{item.title}</span>}
                {isPerson && item.company_name && (
                  <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{item.company_name}</span>
                )}
                {!isPerson && item.domain && (
                  <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{item.domain}</span>
                )}
                {(isPerson ? item.person_location : item.hq_location) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />{isPerson ? item.person_location : item.hq_location}
                  </span>
                )}
                {(isPerson ? item.company_size : item.size) && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />{isPerson ? item.company_size : item.size}
                  </span>
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Industry / tags */}
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(isPerson ? item.company_industry : item.industry) && (
            <Badge variant="secondary">{isPerson ? item.company_industry : item.industry}</Badge>
          )}
          {(isPerson ? item.seniority : null) && (
            <Badge variant="outline">{item.seniority}</Badge>
          )}
          {(isPerson ? [] : (item.tags || item.keywords || [])).slice(0, 5).map(t => (
            <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
          ))}
        </div>

        {/* Contact (people) — email & phone reveal inline; see reveal_price() */}
        {isPerson && !!((item.has_email ?? !!item.email) || item.has_phone
          || item.company_phone || item.corporate_phone || item.linkedin_url) && (
          <div className="mt-4 rounded-lg border border-border bg-surface px-4 py-3 space-y-2">
            <div className="eyebrow">Contact</div>

            {/* Email */}
            {!!(item.has_email ?? !!item.email) && (
              <div className="flex items-center gap-1.5 text-sm">
                <Mail className={`h-3.5 w-3.5 shrink-0 ${item.email_revealed ? "text-success" : "text-muted-foreground"}`} />
                {item.email_revealed
                  ? <a href={`mailto:${item.email}`} className="hover:underline truncate">{item.email}</a>
                  : <RevealInline type="email" onReveal={e => revealEmail(e, item.id)} loading={revealing === `${item.id}:email`} />}
              </div>
            )}

            {/* Every number we hold sits here under the email, each labelled,
                so someone hunting for a phone never has to go looking in the
                firmographics below. Personal lines stay credit-gated; the
                company switchboard is public and shown outright. */}
            {!!item.has_phone && (
              item.phone_revealed ? (
                <>
                  <PhoneRow number={item.mobile_phone}        kind="mobile" />
                  <PhoneRow number={item.work_direct_phone}   kind="direct" />
                  <PhoneRow number={item.work_direct_phone_2} kind="direct" />
                  <PhoneRow number={item.home_phone}          kind="home" />
                </>
              ) : (
                <div className="flex items-center gap-1.5 text-sm">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <RevealInline type="phone" onReveal={e => revealPhone(e, item.id)} loading={revealing === `${item.id}:phone`} />
                </div>
              )
            )}

            {/* Company switchboard: its own cheaper reveal type (029). Priced
                at 1 credit rather than the 5 a direct line costs, because 154
                people share each one. */}
            {item.company_phone_revealed || item.company_phone ? (
              <PhoneRow number={item.company_phone || item.corporate_phone} kind="company" />
            ) : !!item.has_company_phone && (
              <div className="flex items-center gap-1.5 text-sm">
                <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <RevealInline type="company_phone" onReveal={e => revealCompanyPhone(e, item.id)} loading={revealing === `${item.id}:company_phone`} />
                <span className="rounded-xs border border-border bg-surface-inset px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] text-muted-foreground">company</span>
              </div>
            )}

            {!item.has_phone && !item.has_company_phone && !item.company_phone && (
              <div className="text-xs text-muted-foreground">No phone number on file.</div>
            )}

            {item.linkedin_url && (
              <div className="flex items-center gap-1.5 text-sm">
                <Linkedin className="h-3.5 w-3.5 text-violet-text shrink-0" />
                <a href={item.linkedin_url} target="_blank" rel="noreferrer" className="hover:underline truncate text-violet-text">LinkedIn</a>
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="overview" className="mt-5">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="ai">AI</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-3">
            {/* People have no description in the catalog, so showing an empty
                "About" box just wasted the panel. Show every field we actually
                hold instead; InfoCell is skipped wherever the value is absent. */}
            {isPerson ? (
              <>
                <FieldGroup label="Person">
                  {item.title       && <InfoCell label="Title" value={item.title} />}
                  {item.seniority   && <InfoCell label="Seniority" value={item.seniority} />}
                  {item.departments && <InfoCell label="Department" value={item.departments} />}
                  {item.email_status && <InfoCell label="Email status" value={item.email_status} />}
                  {item.first_name  && <InfoCell label="First name" value={item.first_name} />}
                  {item.last_name   && <InfoCell label="Last name" value={item.last_name} />}
                  {item.city        && <InfoCell label="City" value={item.city} />}
                  {item.state       && <InfoCell label="State" value={item.state} />}
                  {item.country     && <InfoCell label="Country" value={item.country} />}
                  {item.zip_code    && <InfoCell label="ZIP" value={item.zip_code} />}
                </FieldGroup>

                <FieldGroup label="Company">
                  {item.company_name          && <InfoCell label="Company" value={item.company_name} />}
                  {item.company_domain        && <InfoCell label="Domain" value={item.company_domain} />}
                  {item.company_industry      && <InfoCell label="Industry" value={item.company_industry} />}
                  {item.company_sub_industry  && <InfoCell label="Sub-industry" value={item.company_sub_industry} />}
                  {item.company_size          && <InfoCell label="Employee band" value={item.company_size} />}
                  {!!item.company_employee_count && <InfoCell label="Employees (est.)" value={item.company_employee_count.toLocaleString()} />}
                  {item.company_revenue_band  && <InfoCell label="Revenue" value={item.company_revenue_band} />}
                </FieldGroup>

                {(item.address || item.company_website || item.linkedin_url || item.source) && (
                  <FieldGroup label="Other">
                    {item.linkedin_url && (
                      <InfoCell label="LinkedIn" value={
                        <a href={item.linkedin_url} target="_blank" rel="noreferrer"
                           className="text-violet-text hover:underline">Profile</a>} />
                    )}
                    {item.address         && <InfoCell label="Address" value={item.address} />}
                    {item.company_website && <InfoCell label="Website" value={item.company_website} />}
                    {item.source          && <InfoCell label="Source" value={item.source} />}
                  </FieldGroup>
                )}
              </>
            ) : (
              <>
                <div className="rounded-xl border border-border bg-surface p-4">
                  <div className="eyebrow flex items-center gap-2">
                    <Building2 className="h-3 w-3" /> About
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                    {item.description || "No description available."}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {!!item.employee_count && <InfoCell label="Employees" value={item.employee_count.toLocaleString()} />}
                  {item.founded_year   && <InfoCell label="Founded" value={item.founded_year} />}
                  {item.total_funding  && <InfoCell label="Total funding" value={`$${(item.total_funding/1e6).toFixed(1)}M`} />}
                  {item.company_type   && <InfoCell label="Type" value={item.company_type} />}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="ai" className="mt-4 space-y-4">
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Research briefing</div>
                <Button size="sm" variant="outline" className="gap-1.5" disabled={aiLoading === "summary"}
                  onClick={() => generate("summary")}>
                  <Sparkles className="h-3.5 w-3.5" />
                  {aiLoading === "summary" ? "Thinking…" : "Generate"}
                </Button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/85">
                {summary || "No briefing yet. Click Generate."}
              </pre>
            </div>
            <div className="rounded-xl border border-border bg-surface p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Outreach draft</div>
                <Button size="sm" variant="outline" className="gap-1.5" disabled={aiLoading === "outreach"}
                  onClick={() => generate("outreach")}>
                  <Sparkles className="h-3.5 w-3.5" />
                  {aiLoading === "outreach" ? "Drafting…" : "Draft email"}
                </Button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/85">
                {outreach || "Click 'Draft email' for a personalized cold email."}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="notes" className="mt-4 space-y-3">
            <Textarea rows={8} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes, conversation history…" />
            <Button onClick={saveNotes}>Save notes</Button>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// One phone line, tap-to-call, with the kind spelled out. A company
// switchboard and a personal mobile are worth very different things to
// someone about to dial, so the label is never optional.
function PhoneRow({ number, kind }) {
  if (!number) return null;
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <Phone className={`h-3.5 w-3.5 shrink-0 ${kind === "company" ? "text-muted-foreground" : "text-success"}`} />
      <a href={`tel:${String(number).replace(/[^\d+]/g, "")}`} className="hover:underline">{number}</a>
      <span className="rounded-xs border border-border bg-surface-inset px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {kind}
      </span>
    </div>
  );
}

// Groups InfoCells under a heading. Renders nothing when every child is
// falsy, so a sparse record does not leave empty labelled boxes behind.
function FieldGroup({ label, children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="eyebrow">{label}</div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">{items}</div>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

// Masked "•••••• click to reveal (i)" control — clicking triggers the reveal.
// `type` drives the price in the tooltip: email and phone are priced
// differently (see reveal_price() in migration 022).
function RevealInline({ onReveal, loading, type = "email" }) {
  const prices = useRevealPrices();
  const price = prices[type] ?? 1;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
      <span className="select-none tracking-[0.2em] text-muted-foreground">••••••</span>
      <button type="button" onClick={onReveal} disabled={loading}
        className="text-xs font-medium text-violet-text hover:underline disabled:opacity-60">
        {loading ? "…" : "click to reveal"}
      </button>
      <span title={`Costs ${price} ${price === 1 ? "credit" : "credits"} to reveal`}
        className="inline-flex cursor-help text-muted-foreground/60">
        <Info className="h-3 w-3" />
      </span>
    </span>
  );
}
